import { useMemo } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import useLocalStorageState from "@hooks/useLocalStorageState";
import { DemographicGroup, ConsumerMetricData } from "@models/ConsumerData";
import CategoryProfileSubtitle from "@pages/CategoryProfile/components/CategoryProfileSubtitle/CategoryProfileSubtitle";
import CategoryProfileSelect from "@pages/CategoryProfile/components/CategoryProfileSelect/CategoryProfileSelect";
import CategoryProfileDataColumns from "@pages/CategoryProfile/components/CategoryProfileDataColumns/CategoryProfileDataColumns";
import {
    DEMOGRAPHIC_OPTIONS,
    DEFAULT_DEMOGRAPHIC,
    buildDemographicStorageKey,
    buildAffinityTooltip,
} from "../../Consumer.constants";
import { buildAffinityColumns } from "../../Consumer.helpers";
import "./ConsumerAffinityIndex.styles.scss";

interface Props {
    categoryId: string;
    metricLabel: string;
    categoryName: string;
    demographics: ConsumerMetricData["demographics"];
}

const ConsumerAffinityIndex: FCX<Props> = ({ categoryId, metricLabel, categoryName, demographics, className }) => {
    const [demographic, setDemographic] = useLocalStorageState<DemographicGroup>(
        buildDemographicStorageKey(categoryId),
        DEFAULT_DEMOGRAPHIC,
        (raw) => {
            const value = Number(raw) as DemographicGroup;
            return DEMOGRAPHIC_OPTIONS.some(option => option.id === value) ? value : DEFAULT_DEMOGRAPHIC;
        },
    );

    const columns = useMemo(
        () => buildAffinityColumns(demographics[demographic] ?? []),
        [demographics, demographic],
    );

    return (
        <div className={cn("ConsumerAffinityIndex", className)}>
            <div className="ConsumerAffinityIndex__header">
                <CategoryProfileSubtitle
                    className="ConsumerAffinityIndex__title"
                    tooltipText={buildAffinityTooltip(metricLabel)}
                >
                    <span className="ConsumerAffinityIndex__title-metric">{metricLabel}</span>
                    {` Consumer Affinity Index: ${categoryName}`}
                </CategoryProfileSubtitle>
                <CategoryProfileSelect
                    options={DEMOGRAPHIC_OPTIONS}
                    value={demographic}
                    setValue={(id) => setDemographic(id as DemographicGroup)}
                    testId="selectCategoryProfileConsumerDemographic"
                />
            </div>

            <CategoryProfileDataColumns
                columns={columns}
                className={cn(
                    "ConsumerAffinityIndex__columns",
                    demographic === DemographicGroup.Gender && "ConsumerAffinityIndex__columns--gender",
                )}
            />
        </div>
    );
};

export default ConsumerAffinityIndex;
